import {
  Button,
  ContextMenu,
  Icon,
  TruncatedTooltip,
} from "@smart/itops-sb-design-system-dom";

import { CustomisedContactDetailsField } from "./customise-helpers";
import { getDuplicateContactWarning } from "./duplicate-helpers";
import { DuplicateWarningLine } from "./duplicate-warning";
import { GqlField, GqlGroup } from "../../types";
import { ContactType } from "../../utils";

export type FieldSetRowProps = {
  name: string;
  layoutId: string;
  roleDisplayName: string;
  contactType: ContactType;
  fields: CustomisedContactDetailsField[];
  disabled?: boolean;
  formFields: GqlField[];
  formGroups: GqlGroup[];
  onAdd: () => void;
  onEdit: () => void;
  onDelete: () => void;
};

export const FieldSetRow = ({
  name,
  layoutId,
  roleDisplayName,
  contactType,
  fields,
  disabled,
  formFields,
  formGroups,
  onAdd,
  onEdit,
  onDelete,
}: FieldSetRowProps) => {
  const duplicateWarning = getDuplicateContactWarning({
    candidates: fields.map(({ fieldName, label }) => ({
      layout: { id: layoutId },
      fieldName,
      label,
    })),
    layoutId,
    contactType,
    roleDisplayName,
    formFields,
    formGroups,
  });
  const canAdd = !disabled && fields.length > 0 && !duplicateWarning;
  const fieldLabels = fields.map((field) => field.label).join(", ");

  return (
    <div className="flex items-center gap-small px-[0.8rem] py-[1.2rem] rounded hover:bg-neutral-20">
      <div className="flex justify-center items-center w-[3.2rem] h-[3.2rem] rounded bg-blue-60">
        <Icon name="regularBookmark" className="text-blue-560" />
      </div>
      <div className="flex flex-col overflow-hidden flex-1 mr-[4rem]">
        <span className="font-semibold truncate">{name}</span>
        <div className="min-w-0 w-full">
          {duplicateWarning ? (
            <DuplicateWarningLine {...duplicateWarning} />
          ) : (
            <TruncatedTooltip
              textToDisplay={
                fieldLabels
                  ? `${roleDisplayName} · ${fieldLabels}`
                  : roleDisplayName
              }
              tooltipText={fieldLabels}
              className="text-caption text-neutral-400 truncate"
              placement="bottom"
              offsetBy={5}
            />
          )}
        </div>
      </div>
      <div className="flex items-center gap-small">
        <Button
          text="Add"
          leftIcon="regularPlus"
          variant="primarySubtle"
          size="small"
          disabled={!canAdd}
          onClick={onAdd}
        />
        <ContextMenu
          id="field-set-row-menu"
          button={{
            variant: "secondarySubtle",
            leftIcon: "solidEllipsis",
            size: "small",
            disabled,
          }}
          placement="bottom-end"
          offsetBy={5}
          items={[
            {
              label: "Edit field set",
              leftIcon: "regularPencil",
              // keep delete available so a colliding set can still be removed
              disabled: !!duplicateWarning,
              onClick: onEdit,
            },
            {
              label: "Delete field set",
              leftIcon: "regularTrashCan",
              className: "text-red-550",
              onClick: onDelete,
            },
          ]}
        />
      </div>
    </div>
  );
};
